package rendering

import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import domain.COLORS
import engine.model.Piece

/**
 * Draws a single [Piece] shape centred inside a small [Canvas] composable.
 *
 * Used for the hold-piece box and the next-piece preview slots in the HUD.
 * The piece is scaled to fit within the available canvas area with a small
 * padding margin.
 *
 * @param piece    The [Piece] to render, or `null` to render an empty box.
 * @param modifier Applied to the [Canvas] composable.
 * @param padding  Fraction of the cell size to use as padding around the piece.
 */
@Composable
fun PiecePreviewCanvas(
    piece: Piece?,
    modifier: Modifier = Modifier,
    padding: Float = 0.15f,
) {
    Canvas(modifier = modifier) {
        if (piece == null) return@Canvas

        val shape = piece.shape
        val rows = shape.rows
        val cols = shape.cols

        // Determine the bounding box of non-zero cells
        var minRow = rows
        var maxRow = -1
        var minCol = cols
        var maxCol = -1
        for (r in 0 until rows) {
            for (c in 0 until cols) {
                if (shape[r, c] != 0) {
                    if (r < minRow) minRow = r
                    if (r > maxRow) maxRow = r
                    if (c < minCol) minCol = c
                    if (c > maxCol) maxCol = c
                }
            }
        }
        if (maxRow < 0) return@Canvas // empty shape

        val usedRows = maxRow - minRow + 1
        val usedCols = maxCol - minCol + 1

        // Compute cell size to fit the bounding box with padding
        val availW = size.width * (1f - padding * 2f)
        val availH = size.height * (1f - padding * 2f)
        val cellSize = minOf(availW / usedCols, availH / usedRows)

        // Centre the piece in the canvas
        val totalW = usedCols * cellSize
        val totalH = usedRows * cellSize
        val originX = (size.width - totalW) / 2f
        val originY = (size.height - totalH) / 2f

        val color = (COLORS[piece.id] ?: 0x888888).toPreviewColor()

        for (r in minRow..maxRow) {
            for (c in minCol..maxCol) {
                if (shape[r, c] == 0) continue
                val x = originX + (c - minCol) * cellSize
                val y = originY + (r - minRow) * cellSize
                drawPreviewCell(x, y, cellSize, color)
            }
        }
    }
}

// ── DrawScope helpers ─────────────────────────────────────────────────────────

/**
 * Draws a single preview cell with highlight and shadow edges, matching the
 * style of [drawCell] in [BoardCanvas].
 */
private fun DrawScope.drawPreviewCell(x: Float, y: Float, cellSize: Float, color: Color) {
    val edgeW = (cellSize * 0.12f).coerceAtLeast(1f)

    // Base fill
    drawRect(color = color, topLeft = Offset(x, y), size = Size(cellSize, cellSize))

    // Highlight (top + left)
    val highlight = Color(
        red = (color.red + 0.25f).coerceAtMost(1f),
        green = (color.green + 0.25f).coerceAtMost(1f),
        blue = (color.blue + 0.25f).coerceAtMost(1f),
        alpha = 0.7f,
    )
    drawRect(color = highlight, topLeft = Offset(x, y), size = Size(cellSize, edgeW))
    drawRect(color = highlight, topLeft = Offset(x, y), size = Size(edgeW, cellSize))

    // Shadow (bottom + right)
    val shadow = Color(
        red = (color.red - 0.25f).coerceAtLeast(0f),
        green = (color.green - 0.25f).coerceAtLeast(0f),
        blue = (color.blue - 0.25f).coerceAtLeast(0f),
        alpha = 0.7f,
    )
    drawRect(color = shadow, topLeft = Offset(x, y + cellSize - edgeW), size = Size(cellSize, edgeW))
    drawRect(color = shadow, topLeft = Offset(x + cellSize - edgeW, y), size = Size(edgeW, cellSize))
}

// ── Color helper ──────────────────────────────────────────────────────────────

private fun Int.toPreviewColor(): Color {
    val r = ((this shr 16) and 0xFF) / 255f
    val g = ((this shr 8) and 0xFF) / 255f
    val b = (this and 0xFF) / 255f
    return Color(r, g, b)
}
